import { Column, Typography } from '@/components'
import { Categories, ThemeColor } from '@/enums'
import { paths } from '@/routes/paths'
import { formatMoney, useGoToPath } from '@/utils'
import { Button, Skeleton } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import {
  ContentCardButtonWrapper,
  ContentCardWrapper,
  NewTag,
  RecommendationCardCarrierLogo,
  RecommendationCardFooter,
  RecommendationCardHeader,
  RecommendationCardHeaderText,
  RecommendationCardLogosStack,
  TextAndLogoSection,
} from '../styles'

interface RecommendationCardProps {
  locationId: string
  recommendationId: string
  categoryId: keyof typeof Categories
  paymentPeriod: string | null
  lowestPrice: number
  carriers: {
    name: string
    logoUrl: string
  }[]
  isNew: boolean
  sended: boolean
}

export const RecommendationCard = ({
  locationId,
  recommendationId,
  categoryId,
  paymentPeriod,
  lowestPrice,
  carriers,
  isNew,
  sended,
}: RecommendationCardProps) => {
  const { t } = useTranslation()
  const categoryTranslation = t(`categories.${Categories[categoryId].key}.name`)
  const categorySubtitle = t(`categories.${Categories[categoryId].key}.offer`)
  const paymentPeriodTranslation = t(`paymentPeriodNouns.${paymentPeriod}`)
  const goToInsuranceCategory = useGoToPath(
    paths.viewInsuranceWithKey({
      categoryId,
      locationId,
    }),
  )
  const goToRecommendation = useGoToPath(
    paths.viewRecommendationWithKey(recommendationId),
  )
  const isDisabled = !sended

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    goToRecommendation()
  }

  return (
    <ContentCardButtonWrapper
      onClick={() => !isDisabled && goToInsuranceCategory()}
      role="button"
    >
      <ContentCardWrapper>
        <RecommendationCardHeader>
          <TextAndLogoSection gap="16px">
            <RecommendationCardHeaderText>
              {isNew && (
                <NewTag>
                  <Typography color={ThemeColor.b0} variant="p2Body">
                    {t('new')}
                  </Typography>
                </NewTag>
              )}
              <Typography
                ellipsis
                bold
                variant="p2Body"
                color={ThemeColor.b100}
              >
                {categoryTranslation}
              </Typography>
            </RecommendationCardHeaderText>
            <RecommendationCardLogosStack>
              {(carriers || []).map((carrier, index) => (
                <>
                  <RecommendationCardCarrierLogo
                    key={`${recommendationId}-${carrier.name}-${index}`}
                    name={carrier.logoUrl}
                  />
                </>
              ))}
            </RecommendationCardLogosStack>
          </TextAndLogoSection>
          <Typography variant="p2Body" color={ThemeColor.b50}>
            {lowestPrice && paymentPeriod
              ? t('locationPage.fromPrice', {
                  price: formatMoney(lowestPrice),
                  period: paymentPeriodTranslation,
                })
              : categorySubtitle}
          </Typography>
        </RecommendationCardHeader>
        <RecommendationCardFooter>
          <Typography variant="p1Body" color={ThemeColor.b100}></Typography>
          <Button
            variant="primary"
            shape="square"
            disabled={isDisabled}
            onClick={handleButtonClick}
          >
            {sended ? t('locationPage.seeOffer') : t('offerInProgress')}
          </Button>
        </RecommendationCardFooter>
      </ContentCardWrapper>
    </ContentCardButtonWrapper>
  )
}

export const RecommendationCardSkeleton = () => {
  return (
    <ContentCardButtonWrapper>
      <RecommendationCardHeader>
        <RecommendationCardHeaderText>
          <Column gap="4px" justifyContent="space-between">
            <Skeleton />
            <Skeleton withParagraphEnding />
          </Column>
        </RecommendationCardHeaderText>
      </RecommendationCardHeader>
      <RecommendationCardFooter>
        <Skeleton />
        <Skeleton />
      </RecommendationCardFooter>
    </ContentCardButtonWrapper>
  )
}
